import { Button, ButtonGroup } from '@components'
import styled, { css } from 'styled-components'

export type RelationMapperThemeMode = 'light' | 'dark'

interface RelationThemeProps {
  $themeMode: RelationMapperThemeMode
}

const relationItemStyles = css<RelationThemeProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.sm}rem;
  text-decoration: none;
  color: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? theme.colors.neutralWhite : 'inherit'};
  transition: background-color 0.15s ease;

  &:hover {
    text-decoration: none;
    background: ${({ theme, $themeMode }) =>
      $themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : theme.colors.defaultHover};
  }
`

export const RelationsContainer = styled.div<{ $compactHeader?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $compactHeader }) => ($compactHeader ? 0 : theme.spacing.xxs)}rem;
`

export const RelationsHeader = styled.div<RelationThemeProps & { $alignEnd?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $alignEnd }) => ($alignEnd ? 'flex-end' : 'space-between')};
  gap: ${({ theme }) => theme.spacing.xs}rem;
  color: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? theme.colors.neutralWhite : theme.colors.neutralBlack};

  h4 {
    margin: 0;
  }
`

export const RelationsMapperContent = styled.div<{ $offsetBelowHeader?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme, $offsetBelowHeader }) => ($offsetBelowHeader ? theme.spacing.xs : 0)}rem;
`

export const RelationListWrapper = styled.div<RelationThemeProps>`
  display: flex;
  flex-direction: column;
  border: 1px solid
    ${({ theme, $themeMode }) =>
      $themeMode === 'dark' ? `${theme.colors.iconGray}55` : theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? 'transparent' : theme.colors.neutralWhite};
  overflow: hidden;

  & > * {
    width: 100%;
  }

  & > * + * {
    border-top: 1px solid
      ${({ theme, $themeMode }) =>
        $themeMode === 'dark' ? `${theme.colors.iconGray}55` : theme.colors.neutralGray};
  }
`

export const RelationItem = styled.div<RelationThemeProps & { $clickable?: boolean }>`
  ${relationItemStyles}
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

export const MutedButtonGroup = styled(ButtonGroup)<RelationThemeProps>`
  flex-shrink: 0;

  & > button {
    color: ${({ theme }) => theme.colors.iconGray};

    &&:hover,
    &&:focus,
    &&:active {
      color: ${({ theme, $themeMode }) =>
        $themeMode === 'dark' ? theme.colors.neutralWhite : theme.colors.metalGray};
      background: ${({ theme, $themeMode }) =>
        $themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : theme.colors.defaultHover};
    }
  }
`

export const RelationIcon = styled.span<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  min-width: 1.75rem;
  height: 1.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme, $color }) => `${$color ?? theme.colors.primary}1a`};
  color: ${({ theme, $color }) => $color ?? theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: ${({ theme }) => theme.fontSize.xs}rem;
    height: ${({ theme }) => theme.fontSize.xs}rem;
  }
`

export const RelationContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

export const RelationLead = styled.p<RelationThemeProps>`
  margin: 0 0 ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 400;
  color: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? theme.colors.neutralWhite : theme.colors.metalGray};

  strong {
    font-weight: 700;
  }
`

export const RelationLeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-wrap: wrap;

  & > p {
    margin: 0;
  }
`

export const SuggestionLeadIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.AIPurple};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  flex-shrink: 0;

  svg {
    width: 1em;
    height: 1em;
  }
`

export const RelationRowPrimary = styled.span<RelationThemeProps>`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? theme.colors.neutralWhite : theme.colors.neutralBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const RelationRowSecondary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`

export const RelationGroupFooter = styled.div<RelationThemeProps & { $warning?: boolean }>`
  display: flex;
  align-items: ${({ $warning }) => ($warning ? 'flex-start' : 'center')};
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid
    ${({ theme, $themeMode, $warning }) =>
      $warning
        ? theme.colors.warning
        : $themeMode === 'dark'
        ? `${theme.colors.iconGray}55`
        : theme.colors.neutralGray};
  background: ${({ theme, $warning }) =>
    $warning ? `${theme.colors.warning}17` : theme.colors.defaultHover};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? theme.colors.neutralWhite : theme.colors.iconGrayDark};
`

export const RelationGroupFooterStatus = styled.span<{
  $variant?: 'success' | 'warning'
  $themeMode?: RelationMapperThemeMode
  $onEmphasizedFooter?: boolean
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme, $variant = 'success', $themeMode, $onEmphasizedFooter }) => {
    if ($onEmphasizedFooter && $themeMode === 'dark') {
      return theme.colors.neutralWhite
    }
    return $variant === 'warning' ? theme.colors.warning : theme.colors.success
  }};

  svg {
    width: ${({ theme }) => theme.fontSize.xxs}rem;
    height: ${({ theme }) => theme.fontSize.xxs}rem;
    flex-shrink: 0;
  }
`

export const MismatchFooterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

export const MismatchFooterTitle = styled.strong`
  display: block;
  font-weight: 600;
  color: inherit;
`

export const MismatchFooterBody = styled.p`
  margin: 0;
  color: inherit;
  line-height: 1.35;
`

export const RelationListWrapperMismatch = styled(RelationListWrapper)`
  border-color: ${({ theme }) => theme.colors.warning};
`

export const RelationListWrapperSuggestion = styled(RelationListWrapper)`
  border-color: ${({ theme }) => theme.colors.AIPurple};
`

export const PickerRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex: 1;
  min-height: 0;
`

export const PickerLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutralWhite};
  flex: 1;
  min-height: 0;
  isolation: isolate;
`

export const PickerListScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem
    ${({ theme }) => theme.borderRadius.md}rem 0 0;
`

export const PickerRow = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  text-align: left;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.md}rem;
  cursor: pointer;
  background: ${({ theme, $isSelected }) =>
    $isSelected ? `${theme.colors.primary}0F` : theme.colors.neutralWhite};
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.metalGray};

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: ${({ theme, $isSelected }) =>
      $isSelected ? `${theme.colors.primary}0F` : theme.colors.defaultHover};
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`

export const PickerCheckbox = styled.span<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.primary : theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  flex-shrink: 0;

  svg {
    width: ${({ theme }) => theme.iconSize.xs}rem;
    height: ${({ theme }) => theme.iconSize.xs}rem;
  }
`

export const PickerRowBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex: 1;
  min-width: 0;
`

export const PickerRowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  width: 100%;
`

export const PickerNumber = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  min-width: 0;
`

export const PickerDate = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  white-space: nowrap;
`

export const PickerContact = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

export const PickerAmount = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const PickerDescription = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const PickerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme }) => theme.colors.defaultHover};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  flex-shrink: 0;
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.md}rem
    ${({ theme }) => theme.borderRadius.md}rem;
`

export const PickerFooterAmount = styled.span<{ $status?: 'exact' | 'over' | 'under' }>`
  font-weight: 600;
  color: ${({ theme, $status }) =>
    $status === 'exact'
      ? theme.colors.success
      : $status === 'over'
      ? theme.colors.warning
      : theme.colors.metalGray};
`

export const PickerEmpty = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const PickerLoadingMore = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xs}rem;
`

export const EmptyState = styled.div<RelationThemeProps>`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme, $themeMode }) =>
    $themeMode === 'dark' ? theme.colors.neutralWhite : theme.colors.metalGray};
`

/** Form-only: dashed empty box with inline Linkitä action. */
export const FormEmptyBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.625rem;
  padding: 0.6875rem 0.75rem 0.6875rem 0.875rem;
  border: 1.5px dashed ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

export const FormEmptyLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  white-space: nowrap;
  min-width: 0;

  svg {
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
  }
`

export const FormSuggestionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
`

export const FormFallbackTrigger = styled(Button)`
  && {
    align-self: flex-start;
    height: auto;
    min-height: 0;
    padding: 0;
    border: none;
    background: none;
    box-shadow: none;
    color: ${({ theme }) => theme.colors.iconGray};
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    font-weight: 400;

    &:hover,
    &:focus,
    &:active {
      background: none;
      border: none;
      box-shadow: none;
      color: ${({ theme }) => theme.colors.metalGray};
      text-decoration: underline;
    }
  }
`

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  height: 100%;
  min-height: 0;

  & > * {
    margin: 0;
  }
`

export const ModalFooter = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`
